import React, { useEffect, useState } from "react";
import { observer, inject } from "mobx-react";
import { useTranslation } from "react-i18next";
import LoadingContainer from "../layouts/LoadingContainer";
import { useMobile } from "../utils/mobile";
import ReportDetails from "./ReportDetails";
import ReportDetailsMobile from "./ReportDetailsMobile";
import ConfirmModal from "../components/ConfirmModal";
import { matchesDateRange, matchesSearch } from "../UI/Input/SearchInput";

// Fields shown in the reports table and the mobile rows (ZCOR_TASKDES and ZCOR_DOCNO columns
// display ZCOR_TASKNAME and DOCNO)
const SEARCH_FIELDS = [
  "CUSTDES",
  "ORDNAME",
  "PARTDES",
  "JOBDESC",
  "ZCOR_TASKNAME",
  "DOCNO",
  "ZCOR_DOCNO",
  "HOURS",
  "ANSWER",
  "QDES",
];

const SEARCH_TAB = "3";

const isFilterActive = (search, date) => !!search?.trim() || !!date[0] || !!date[1];

const ReportDetailsMain = ({ userStore, reportDetailsStore }) => {
  const { t } = useTranslation("report_details");
  const [isLoading, setLoading] = useState(true);
  const [isListLoading, setListLoading] = useState(false);
  const [date, setDate] = useState([null, null]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedReport, setSelectedReport] = useState({});
  const [tab, setTab] = useState("1");
  // Tab to return to when the search / date filter is cleared
  const [tabBeforeSearch, setTabBeforeSearch] = useState("1");
  const [reportToDelete, setReportToDelete] = useState(null);
  const isMobile = useMobile();

  useEffect(() => {
    const fetchReportDetails = async () => {
      setLoading(true);
      reportDetailsStore.setError("");
      try {
        await reportDetailsStore.getReportDetails(userStore.userDetails);
        if (!!reportDetailsStore.error) {
          setError(reportDetailsStore.error);
        }
      } catch (error) {
        console.error("Failed to fetch report details", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReportDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Current / last month tabs show every report; only the search tab is filtered
  const allReports = reportDetailsStore.reportDetails || [];
  const inSearch = isFilterActive(search, date);
  const searchResults = inSearch
    ? allReports.filter(
        (report) =>
          matchesDateRange(report.CURDATE, date) && matchesSearch(report, SEARCH_FIELDS, search)
      )
    : [];

  // Jump to the search tab when a filter starts, and back to the previous tab when it is cleared
  const applyFilter = (nextSearch, nextDate) => {
    const wasActive = isFilterActive(search, date);
    const willBeActive = isFilterActive(nextSearch, nextDate);

    if (!wasActive && willBeActive) {
      setTabBeforeSearch(tab);
      setTab(SEARCH_TAB);
    } else if (wasActive && !willBeActive && tab === SEARCH_TAB) {
      setTab(tabBeforeSearch);
    }

    setSearch(nextSearch);
    setDate(nextDate);
  };

  const handelRowClick = (report) => {
    const reportData = {
      REPORT: report.REPORT,
      LINE: report.LINE,
      data: {
        CURDATE: report.CURDATE,
        CUSTNAME: report.CUSTNAME,
        CUSTDES: report.CUSTDES,
        ORDNAME: report.ORDNAME,
        DETAILS: report.DETAILS,
        PARTDES: report.PARTDES,
        PDES: report.PDES,
        OLINE: report.OLINE,
        JOBDESC: report.JOBDESC,
        // The edit form joins JOBDESC + JOBDESCLONG; without this a long description was cut
        // to its first part and saving overwrote the rest
        JOBDESCLONG: report.JOBDESCLONG,
        HOURS: report.HOURS,
        QDES: report.QDES,
        ANSWER: report.ANSWER,
        ZCOR_TASKNAME: report.ZCOR_TASKNAME,
        ZCOR_TASKDES: report.ZCOR_TASKDES,
        DOCNO: report.ZCOR_DOCNO,
        FREEOFCHARGE: report.FREEOFCHARGE,
      },
    };
    setSelectedReport(reportData);
  };

  const handelOpenNewReportClick = (report) => {
    setSelectedReport({});
  };

  // Asks for confirmation first; the delete itself runs in confirmDelete
  const handelRowDeleteClick = (e, report) => {
    e.preventDefault();
    e.stopPropagation();
    setReportToDelete(report);
  };

  const confirmDelete = async () => {
    const report = reportToDelete;
    setReportToDelete(null);
    setListLoading(true);
    setError("");
    reportDetailsStore.setError("");
    try {
      await reportDetailsStore.deleteReport(report);
      if (!!reportDetailsStore.error) {
        setError(reportDetailsStore.error);
      }
    } catch (error) {
      console.error("Failed to delete report", report, error);
      setError("error:error_failed_delete_report_req");
    } finally {
      setListLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    applyFilter(value, date);
  };

  // Mobile sends [{ startDate, endDate }], the desktop date range picker sends [from, to]
  const handleDateChange = (value) => {
    if (!value?.length) {
      applyFilter(search, [null, null]);
    } else if (value[0]?.startDate !== undefined) {
      applyFilter(search, [value[0].startDate, value[0].endDate]);
    } else {
      applyFilter(search, [value[0] ?? null, value[1] ?? null]);
    }
  };

  const viewProps = {
    allReports,
    searchResults,
    inSearch,
    tab,
    setTab,
    isListLoading,
    selectedReport,
    handelRowClick,
    handelRowDeleteClick,
    handelOpenNewReportClick,
    handleSearchChange,
    handleDateChange,
    search,
    date,
    error,
  };

  return (
    <LoadingContainer isLoading={isLoading}>
      {isMobile ? <ReportDetailsMobile {...viewProps} /> : <ReportDetails {...viewProps} />}
      {!!reportToDelete && (
        <ConfirmModal
          title={t("delete_report_title")}
          text={t("delete_q")}
          handleAction={confirmDelete}
          onClose={() => setReportToDelete(null)}
        />
      )}
    </LoadingContainer>
  );
};

export default inject(
  "userStore",
  "reportDetailsStore"
)(observer(ReportDetailsMain));
